/* eslint-disable max-lines */
import {PAYMENT_METHODS} from '@optimaxdev/analytics/desktop/constants/paymentMethods';
import {prepareDetailDataForTYP} from '@optimaxdev/analytics/desktop/helpers/prepareDetailDataForTYP/index';
import {createEECPurchaseEvent} from '@optimaxdev/analytics/desktop/lib/ecommerce/typ/index';
import {pushDataLayer} from '@optimaxdev/analytics/desktop/lib/pushDataLayer/index';
import {
    BasicProductType,
    PurchaseActionFieldType,
} from '@optimaxdev/analytics/desktop/types/ecommerce';

import {LOCAL_STORAGE_CHECKOUT} from 'constants/analytics';
import {ITEMS_WITH_PRESCRIPTION_TYPE_KEY, QUICK_CHECKOUT_INITIALIZED} from 'constants/constants';
import {CartItemType, CartType} from 'desktop/pages/cart/types';
import {OrderLastType} from 'desktop/pages/success/types/types';
import {RouterType} from 'hostApplicationStoreType';
import {getAffId, normalizeAffId} from 'libs/analytics/affiliate';
import {getRevenueValue} from 'libs/analytics/getOrderData';
import {pushVizury} from 'libs/analytics/vizury';
import {PAGE_NAME_FOR_VIZURY} from 'libs/analytics/vizury.types';
import {getLatestCartItem} from 'libs/getLatestCartItem/getLatestCartItem';
import {flags} from 'libs/rollout';
import {logWithSentry, logWithSentryEEC} from 'libs/sentry/sentry';
import {localStore} from 'libs/storage/storage';

/**
 * Prepare product data for analytics
 * @param {CartType} order - data about the cart saved on the previous page
 * @returns {SagaIterator} - prepare products
 */
export const prepareProductData = (order: CartType, orderLastData: OrderLastType) => {
    const orderProductsWithPrescription = localStore.get(ITEMS_WITH_PRESCRIPTION_TYPE_KEY);
    const preparedData = order?.quoteItems?.map((quoteItem: CartItemType) =>
        prepareDetailDataForTYP({
            orderItemId: orderLastData?.items?.find?.(
                orderItem => orderItem.quoteItemId === Number(quoteItem.id),
            )?.id,
            orderProductsWithPrescription,
            product: quoteItem,
            quantity: Number(quoteItem.qty),
        }),
    );
    localStore.remove(ITEMS_WITH_PRESCRIPTION_TYPE_KEY);
    return preparedData;
};

/**
 * Prepare purchase data for analytics
 * @param {CartType} order - data about the cart saved on the previous page
 * @returns {PurchaseActionFieldType} - prepared order data
 */
export function preparePurchaseData(
    order: CartType,
    router: RouterType,
    orderLastData: {
        orderId: string;
        taxAmount: number;
    },
): PurchaseActionFieldType {
    const {orderId: id, taxAmount: tax} = orderLastData;

    const search = router?.location?.search || '';
    const checkoutJSON = localStore.get(LOCAL_STORAGE_CHECKOUT) || '{}';
    const affId = getAffId({search});

    const {review} = JSON.parse(checkoutJSON);

    return {
        action: 'purchase',
        affiliation: normalizeAffId(affId),
        coupon: (order?.totals?.appliedCoupons || [])
            .map((item: string) => item.toUpperCase())
            .join(','),
        id,
        revenue: String(getRevenueValue(review)),
        shipping: String(review?.shipping || 0),
        tax: typeof tax === 'number' ? tax.toFixed(4) : '0.0000',
    };
}

/**
 * Error logging function for sentry if we have incorrect revenue
 * @param {ReturnType<typeof sessionStore.get>} order - order list
 * @param {string} revenue - revenue string value
 */
export function logWithSentryForErroneousRevenueOrOrder(order: unknown): boolean {
    if (!order) {
        logWithSentry(new Error('Analytics. EEC_purchase. No data on regular order'));
        return true;
    }

    return false;
}

type PreparationDataForPurchaseEventPropsType = {
    order: CartType;
    revenue: string;
    router: RouterType;
    id: string;
    orderLastData: OrderLastType;
};

/**
 * Preparation of data for EEC_purchase event
 * @param {ReturnType<typeof sessionStore.get>} order - order list
 * @param {string} revenue - revenue string value
 * @returns {PreparationDataForEventType} return data
 */
export function preparationDataForPurchaseEvent({
    id: orderId,
    order,
    orderLastData,
    revenue,
    router,
}: PreparationDataForPurchaseEventPropsType) {
    const isQuickCheckout: boolean = localStore.get(QUICK_CHECKOUT_INITIALIZED);
    const updatedOrder = isQuickCheckout
        ? {...order, quoteItems: getLatestCartItem<CartItemType>(order.quoteItems)}
        : order;

    const actionField: PurchaseActionFieldType = preparePurchaseData(updatedOrder, router, {
        orderId,
        taxAmount: orderLastData.taxAmount,
    });
    const products: BasicProductType[] = prepareProductData(updatedOrder, orderLastData);

    return {
        actionField: {...actionField, revenue},
        isNewCustomer: orderLastData.isNewCustomer,
        orderId,
        products,
    };
}

/**
 * Send data for EC
 */
export function sendEcData() {
    if (typeof window.ga === 'undefined') return;

    window.ga('require', 'ec');
}

type SendEventPropsType = {
    checkoutData: {paymentMethod: string};
    order: CartType;
    placeOfInteraction: string;
    revenue: string;
    id: string;
    router: RouterType;
    orderLastData: OrderLastType;
};

/**
 * Helper for sending all necessary events
 * Must be used only in sendEventForRegularOrder
 */
export const sendEvent = ({
    checkoutData,
    id,
    order,
    orderLastData,
    placeOfInteraction,
    revenue,
    router,
}: SendEventPropsType) => {
    const {actionField, isNewCustomer, orderId, products} = preparationDataForPurchaseEvent({
        id,
        order,
        orderLastData,
        revenue,
        router,
    });

    const eecData = {
        actionField,
        isNewCustomer,
        orderId: actionField.id,
        paymentMethod:
            checkoutData?.paymentMethod === 'recurly_paypal'
                ? checkoutData?.paymentMethod
                : PAYMENT_METHODS[checkoutData?.paymentMethod || ''],
        products,
    };

    pushDataLayer({
        ...createEECPurchaseEvent(eecData),
        placeOfInteraction,
    });

    if (!flags.dDisableGaEc.isEnabled()) {
        sendEcData();
    }

    pushVizury({
        data: {
            id: orderId,
            orderedItems: order?.quoteItems,
            revenue,
        },
        location: PAGE_NAME_FOR_VIZURY.TYP,
    });

    logWithSentryEEC('purchase', eecData);
};
